/**
 * Confirms which pins go to the measurement queue and who measures them.
 *
 * The two answers are not the same action: measuring them yourself only moves
 * pins into your own queue, while having ApexOS measure them is a purchase that
 * charges the card on file the moment it is confirmed. So this dialog doubles
 * as the charge confirmation for the paid path, and states the amount on the
 * button that places it.
 *
 * Joy's Modal portals to document.body, which matters here: the page's glass
 * cards use backdrop-filter, and a fixed overlay rendered inside one of them
 * would be positioned against the card instead of the viewport.
 */

import React from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalDialog,
  Radio,
  RadioGroup,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import { formatMoney, moneyToNumber } from '../../lib/directMailApi';
import type { MailFulfillment, MailProductRate } from '../../types/directMail';
import { MAIL_FULFILLMENT_LABELS } from '../../types/directMail';

export type QueueScope = 'session' | 'campaign';

export interface SendPinsToQueueChoice {
  scope: QueueScope;
  fulfillment: MailFulfillment;
}

export interface SendPinsToQueueModalProps {
  open: boolean;
  onClose: () => void;
  sessionCount: number;
  campaignCount: number;
  /** The `measurement_service` catalog row; null while loading or if unpriced. */
  rate: MailProductRate | null;
  /** Ordering charges the card, so only an organization admin may place it. */
  canOrder: boolean;
  submitting: boolean;
  onConfirm: (choice: SendPinsToQueueChoice) => void;
}

export const SendPinsToQueueModal: React.FC<SendPinsToQueueModalProps> = ({
  open,
  onClose,
  sessionCount,
  campaignCount,
  rate,
  canOrder,
  submitting,
  onConfirm,
}) => {
  const [scope, setScope] = React.useState<QueueScope>('session');
  const [fulfillment, setFulfillment] = React.useState<MailFulfillment>('self');

  React.useEffect(() => {
    if (!open) return;
    setScope(sessionCount > 0 ? 'session' : 'campaign');
    setFulfillment('self');
  }, [open, sessionCount]);

  const count = scope === 'session' ? sessionCount : campaignCount;
  const unitPrice = rate ? moneyToNumber(rate.price) : null;
  const orderTotal = unitPrice === null ? null : unitPrice * count;
  const buying = fulfillment === 'apexos';
  const canConfirm = count > 0 && (!buying || (canOrder && orderTotal !== null));

  return (
    <Modal open={open} onClose={submitting ? () => undefined : onClose}>
      <ModalDialog sx={{ maxWidth: 520, width: '100%' }}>
        <DialogTitle>Send pins to the measurement queue</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Stack spacing={1}>
              <Typography level="title-sm">Which pins</Typography>
              <RadioGroup
                value={scope}
                onChange={(event) => setScope(event.target.value as QueueScope)}
              >
                <Radio
                  value="session"
                  disabled={sessionCount === 0}
                  label={`Pinned in this session (${sessionCount})`}
                />
                <Radio
                  value="campaign"
                  disabled={campaignCount === 0}
                  label={`Every unqueued pin in this campaign (${campaignCount})`}
                />
              </RadioGroup>
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Typography level="title-sm">Who measures the roofs</Typography>
              <RadioGroup
                value={fulfillment}
                onChange={(event) => setFulfillment(event.target.value as MailFulfillment)}
              >
                <Radio value="self" label={MAIL_FULFILLMENT_LABELS.self} />
                <Radio
                  value="apexos"
                  disabled={!canOrder}
                  label={MAIL_FULFILLMENT_LABELS.apexos}
                />
              </RadioGroup>
              {fulfillment === 'self' ? (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Pins land in your measurement queue for anyone on your team to claim.
                </Typography>
              ) : (
                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                  {orderTotal === null ? (
                    <Typography level="body-sm">
                      ApexOS measurement pricing is unavailable right now.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography level="body-sm">Roofs</Typography>
                        <Typography level="body-sm">{count}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography level="body-sm">Price per roof</Typography>
                        <Typography level="body-sm">{formatMoney(rate?.price)}</Typography>
                      </Stack>
                      <Divider />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography level="title-sm">Charged to your card on file</Typography>
                        <Typography level="title-sm">{formatMoney(orderTotal)}</Typography>
                      </Stack>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Our team measures each roof from satellite imagery and prices it from your
                        own Roof Quote tiers. The card on file is charged as soon as you confirm.
                      </Typography>
                    </Stack>
                  )}
                </Sheet>
              )}
              {!canOrder && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Only an organization admin can order ApexOS measurements, because placing the
                  order charges the card on file.
                </Typography>
              )}
              {buying && count > 0 && (
                <Alert color="neutral" variant="soft">
                  Every one of these {count} pins is ordered and paid for together. Deselect the
                  campaign-wide option if you only meant the houses you just pinned.
                </Alert>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onConfirm({ scope, fulfillment })}
            loading={submitting}
            disabled={!canConfirm}
          >
            {buying
              ? `Charge ${orderTotal === null ? '—' : formatMoney(orderTotal)} and order ${count}`
              : `Queue ${count} ${count === 1 ? 'pin' : 'pins'}`}
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
